// src/components/steps/Register.jsx
import React, { useState, useEffect, useRef } from "react";
import HorizontalTimeline from "../HorizontalTimeline";
import BasketSnippet from "../BasketSnippet";
import config from "../../config/properties.json";
import BasketsApi from "../../api/bff/src/api/BasketsApi";
import ApiClient from "../../api/bff/src/ApiClient";
import ConfirmedBasketsApi from "../../api/bff/src/api/ConfirmedBasketsApi";
import { loadStripe } from "@stripe/stripe-js";
import { Tooltip } from '@mui/material';
import ProductsApi from "../../api/bff/src/api/ProductsApi";
import AddonsApi from "../../api/bff/src/api/AddonsApi";
import { default as content } from '../../content/en';
import { FadeLoader, ScaleLoader } from "react-spinners";

import { AddressSearchBar } from "../AddressSearchBar";
import PostGridAddressAutocomplete from "../PostGridAutoComplete";

const apiClient = new ApiClient(config.apiBaseUrl);
const basketsApi = new BasketsApi(apiClient);
const confirmedBasketsApi = new ConfirmedBasketsApi(apiClient);
const productsApi = new ProductsApi(apiClient);
const addOnsApi = new AddonsApi(apiClient);

const signupProductLegacyId = "BROADBANDGO-PREPAID-CUST-SIGNUP";
const pSimDispatchLegacyId = "ESIMGO-DISPATCH-SIM-PREPAID";
const discountProductLegacyId = "APPLY-DISCOUNT";

export default function CustomerDetails({
  next,
  prev,
  userEmail,
  userPlan,
  setUserPlan,
  planPrice,
  setPlanPrice,
  customerSelections,
  setCustomerSelections,
  customerDetails,
  setCustomerDetails,
  basket,
  setBasket,
  simType,
  setSimType,
  affilliateRef,
  promoCode
}) {
  const searchParams = new URLSearchParams(window.location.search);
  const paymentStatus = searchParams.get("paymentStatus");
  const confirmedBasketId = localStorage.getItem("confirmedBasketId");
  const [existingConfirmedBasket, setExistingConfirmedBasket] = useState(undefined);
  const [signUpProduct, setSignUpProduct] = useState(undefined);
  const [simDispatchProduct, setSimDispatchProduct] = useState(undefined);
  const [discountProduct, setDiscountProduct] = useState(undefined);
  const [plans, setPlans] = useState(undefined);
  const simTypeSelector = useRef(null);
  const [submitClicked, setSubmitClicked] = useState(false);
  const [discountValidated, setDiscountValidated] = useState(false);
  const [discountProcessing, setDiscountProcessing] = useState(false);
  const [discountError, setDiscountError] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(undefined);
  const [basketLoading, setBasketLoading] = useState(!basket);

  const discountErrorsPrioritised = [
    'PROMO_NOT_AVAILABLE',
    'PROMO_MAX_REDEMPTIONS_REACHED',
    'COUPON_MAX_REDEMPTIONS_REACHED',
    'PROMO_MINIMUM_AMOUNT_NOT_MET',
    'COUPON_PRODUCT_RESTRICTION',
    'PROMO_CUSTOMER_RESTRICTION',
    'PROMO_MINIMUM_AMOUNT_CURRENCY_MISMATCH',
    'PROMO_EXPIRED',
    'PROMO_NOT_ACTIVE',
    'COUPON_NOT_FOUND',
    'COUPON_NOT_ACTIVE',
    'COUPON_EXPIRED',
    'COUPON_NOT_VALID',
  ]

  function getHighestPriorityErrorCode(validationErrors) {
    for (const errorCode of discountErrorsPrioritised) {
      if (validationErrors.filter(e => e.code === errorCode).length > 0) {
        return errorCode
      }
    }
    return 'PROMO_NOT_AVAILABLE'
  }


  async function getSignUpProduct() {
    const signUpProducts = await productsApi.productsGet({ "category": "signupprepaidcustomer" })
    const signUpProduct = signUpProducts.find(p => p.legacyId === signupProductLegacyId);
    setSignUpProduct(signUpProduct);
    return signUpProduct;
  }

  async function getSimDispatchProduct() {
    const simDispatchProducts = await productsApi.productsGet({ "category": "dispatchsim" });
    const simDispatchProduct = simDispatchProducts.find(p => p.legacyId === pSimDispatchLegacyId);
    setSimDispatchProduct(simDispatchProduct);
    return simDispatchProduct;
  }

  async function getDiscountProduct() {
    const discountProducts = await productsApi.productsGet({ "category": "modifyservicedetails" });
    const discountProduct = discountProducts.find(p => p.legacyId === discountProductLegacyId);
    setDiscountProduct(discountProduct);
    return discountProduct;
  }

  async function fetchConfirmedBasket() {
    if (confirmedBasketId) {
      let confBasket = await confirmedBasketsApi.confirmedbasketsIdGet(confirmedBasketId);
      setExistingConfirmedBasket(confBasket);
    }
  }

  function clearConfirmedBasket() {
    if (confirmedBasketId) {
      localStorage.removeItem("confirmedBasketId");
    }
    setExistingConfirmedBasket(undefined);
  }

  async function getPlans() {
    const plans = await addOnsApi.addonsGet();
    setPlans(plans);
  }

  //The below is to handle coming back from Stripe and setting up all of our state values again
  //This is needed because we have set up a confirmed basket to get to Stripe, which can no longer be changed
  //so we need to set up a new basket that is not confirmed
  useEffect(() => {
    const fetchDataIfNeeded = () => {
      if (existingConfirmedBasket === undefined) fetchConfirmedBasket();
      if (signUpProduct === undefined) getSignUpProduct();
      if (simDispatchProduct === undefined) getSimDispatchProduct();
      if (plans === undefined) getPlans();
      if (discountProduct === undefined) getDiscountProduct();
    };

    // 1. Run during normal React mount or dependency update
    fetchDataIfNeeded();

    // 2. Setup listener in case the browser restores the page from bfcache (Back/Forward Cache)
    const handlePageShow = (event) => {
      if (event.persisted) {
        console.log('event is persisted')
        window.location.reload();
        // fetchDataIfNeeded();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [
    existingConfirmedBasket,
    signUpProduct,
    simDispatchProduct,
    plans,
    discountProduct
  ]);

  const storedCustomerSelections = JSON.parse(localStorage.getItem("customerSelections"));
  if (storedCustomerSelections !== null) {
    setCustomerSelections(storedCustomerSelections);
    localStorage.removeItem("customerSelections");
  }

  async function createNewBasketFromProducts(accountProducts, serviceProducts) {
    // if (products === undefined || products.length === 0) {
    //   return;
    // }
    const basketsPostRequest = {
      accountItems: accountProducts,
      serviceItems: serviceProducts,
      channel: config.channel
    }

    const basket = await basketsApi.basketsPost({ basketsPostRequest });
    console.log("createNewBasketFromProducts created basket: " + JSON.stringify(basket, null, 2));
    setBasket(basket);
  }

  function removeDiscountsFromBasket() {
    const basketServiceItems = basket.serviceItems
    const basketServiceItemsWithoutDiscount = basketServiceItems.filter(item => {
      let extData = item.extendedData;
      if (typeof extData === 'string') {
        try { extData = JSON.parse(extData); } catch (e) { extData = {}; }
      }
      return extData?.promoCode === undefined || extData?.promoCode === "";
    });
    setAppliedDiscount(undefined);
    basket.serviceItems = basketServiceItemsWithoutDiscount;
    setBasket(basket);
    setDiscountValidated(false);
  }

  async function applyOrRemoveDiscount() {
    // if (form.discountCode === null || form.discountCode?.trim() === "") {
    //   setDiscountError("Please enter a discount code");
    //   return;
    // }

    setDiscountProcessing(true);
    setDiscountError(null);

    if (discountValidated) {
      //already have a discount applied, so remove it
      console.log('discount validated, so removing it');
      const basketServiceItems = basket.serviceItems
      const basketServiceItemsWithoutDiscount = basketServiceItems.filter(item => {
        let extData = item.extendedData;
        if (typeof extData === 'string') {
          try { extData = JSON.parse(extData); } catch (e) { extData = {}; }
        }
        return extData?.promoCode === undefined || extData?.promoCode === "";
      });
      console.log('basketServiceItemsWithoutDiscount ' + JSON.stringify(basketServiceItemsWithoutDiscount, null, 2));
      setForm({ ...form, discountCode: "" });
      setAppliedDiscount(undefined);
      basket.serviceItems = basketServiceItemsWithoutDiscount;
      setBasket(basket);
      setDiscountValidated(false);
    } else {
      //apply discount
      console.log('discount not yet validated, so adding it');

      //firstly, if the discount product has not been fetched yet we need to fetch it
      const fetchedDiscountProduct = await getDiscountProduct();

      console.log('fetchedDiscountProduct is: ' + JSON.stringify(fetchedDiscountProduct, null, 2));

      //trim any leading or trailing whitespace from the code
      const discountExtendedData = {
        promoCode: form.discountCode?.trim(),
        emailAddress: form.email,
      }
      const basketServiceItems = basket.serviceItems
      const basketServiceItemsWithDiscount = [...basketServiceItems, { productId: Number(fetchedDiscountProduct.id), extendedData: discountExtendedData }];
      basket.serviceItems = basketServiceItemsWithDiscount;
      setBasket(basket);

      try {
        console.log('validating basket: ' + JSON.stringify(basket, null, 2));
        const validationResponse = await basketsApi.basketsValidatePost(basket);
        console.log("validationResponse: " + JSON.stringify(validationResponse, null, 2));

        //any successful non-error response means validation has passed according to the API doc
        setDiscountValidated(true);
        setAppliedDiscount(validationResponse.coupon)
        localStorage.setItem("appliedDiscount", JSON.stringify(validationResponse.coupon));

        //update the basket with the discount in the BFF
        //SB - we aren't doing this here, it gets handled later on. But this statement is useful for debugging without moving on to stripe
        // const basketResponse = await basketsApi.basketsIdPut(basket.basketId, basket);
        // console.log('basket after discount applied: ' + JSON.stringify(basketResponse, null, 2));
        // setBasket(basketResponse);

      } catch (error) {
        console.log("Error validating discount: " + JSON.stringify(error, null, 2));
        //remove the discounts from the in-memory basket
        const basketServiceItemsWithoutDiscount = basketServiceItems.filter(item => item.productId !== discountProduct.id);
        setBasket({ ...basket, serviceItems: basketServiceItemsWithoutDiscount });
        if (error.body?.validationErrors?.length > 0 && error.body?.validationErrors[0]?.errors?.length > 0) {
          const errorCode = getHighestPriorityErrorCode(error.body?.validationErrors[0]?.errors);
          console.log("errorCode: " + errorCode);
          const errorMessage = content.errors.promoCodeErrors[errorCode] || "Other Problem";
          setDiscountError(errorMessage);
        } else {
          setDiscountError('Invalid Discount Code');
        }
        setDiscountValidated(false);
      }
    }

    setDiscountProcessing(false);
  }

  async function confirmBasketAndProceed() {
    /* extended data format:
    { "accountItems": [{ "productId": 72, "extendedData": {"firstName": "Sam", "lastName": "Smith", "emailAddress": "sam@gmail.com", "dateOfBirth": "2000-03-07"} }, 
                       { "productId": 74, "extendedData": {"deliveryAddress": "21, Locksons close E14 6BH London"} }] }
     */

    try {
      const accountItems = []

      const isPhysicalSim = simType === "physical";

      const customerDetailsExtendedData = {
        firstName: form.firstName,
        lastName: form.lastName,
        emailAddress: form.email,
        affiliateReferalCode: affilliateRef || "",
        activationProductId: userPlan.id,
        pSim: isPhysicalSim ? 1 : 0,
      }

      //const signUpProducts = await productsApi.productsGet({"category": "signupprepaidcustomer"})
      //const signUpProduct = signUpProducts.find(p => p.legacyId === signupProductLegacyId);

      let localSignUpProduct = undefined;
      if (signUpProduct === undefined) {
        console.log("signUpProduct is undefined - calling getSignUpProduct()");
        localSignUpProduct = await getSignUpProduct();
      }

      //console.log("localSignUpProduct.id = " + localSignUpProduct?.id + ", signUpProduct.id = " + signUpProduct?.id);
      const signUpItem = {
        productId: localSignUpProduct?.id || signUpProduct?.id,
        extendedData: customerDetailsExtendedData
      }

      accountItems.push(signUpItem);



      if (isPhysicalSim) {
        const deliveryAddressExtendedData = { deliveryAddress: form.deliveryAddress };
        //const simDispatchProducts = await productsApi.productsGet({"category": "dispatchsim"});
        //const simDispatchProduct = simDispatchProducts.find(p => p.legacyId === pSimDispatchLegacyId);
        let localSimDispatchProduct = undefined;
        if (simDispatchProduct === undefined) {
          console.log("simDispatchProduct is undefined - calling getSimDispatchProduct()");
          localSimDispatchProduct = await getSimDispatchProduct();
        }

        //console.log("localSimDispatchProduct.id = " + localSimDispatchProduct?.id + ", simDispatchProduct.id = " + simDispatchProduct?.id);
        const simDispatchItem = {
          productId: localSimDispatchProduct?.id || simDispatchProduct?.id,
          extendedData: deliveryAddressExtendedData
        }

        accountItems.push(simDispatchItem);
      }

      let localCustomerSelections = ({
        ...customerSelections,
        ["Your Details"]: {
          stepDescription: "SIM Type = " + (simType === "physical" ? "plastic SIM" : "eSIM")
        },
      });
      setCustomerSelections(localCustomerSelections);

      if (basket.accountItems === undefined) {
        basket.accountItems = accountItems
      } else {
        basket.accountItems.push(...accountItems);
      }

      //update the basket with the new account items
      console.log('sending basket through to the BFF: ' + JSON.stringify(basket, null, 2));
      basket = await basketsApi.basketsIdPut(basket.basketId, basket);

      const confirmedBasket = await basketsApi.basketsIdConfirmPost(basket.basketId);
      const { sessionId } = await confirmedBasketsApi.confirmedbasketsIdHostedPaymentPost(confirmedBasket.basketId);

      //Now that we're navigating away to Stripe, we need to store out basket ID in local storage in case we get redirected back
      localStorage.setItem("confirmedBasketId", confirmedBasket.basketId);
      localStorage.setItem("customerSelections", JSON.stringify(localCustomerSelections));

      const stripe = await loadStripe(config.stripePublicKey);
      stripe.redirectToCheckout({ sessionId: sessionId });

    } catch (error) {
      console.error("Error confirming basket:", error);
      setSubmitClicked(false);
      return;
    }
  }


  const [form, setForm] = useState({ firstName: "", email: userEmail, lastName: "", deliveryAddress: "", discountCode: promoCode });
  useEffect(() => {

    if (existingConfirmedBasket !== undefined && customerDetails === undefined && signUpProduct !== undefined && simDispatchProduct !== undefined && plans !== undefined) {
      console.log('existingConfirmedBasket has been loaded ' + JSON.stringify(existingConfirmedBasket, null, 2));
      try {
        let accountProductsFromConfirmedBasket = [];
        let serviceProductsFromConfirmedBasket = [];
        let localCustomerDetails;
        const accountItems = existingConfirmedBasket?.accountItems.map((accountItem) => {
          const customerExtendedData = accountItem?.extendedData;
          const extendedDataJson = JSON.parse(customerExtendedData);
          console.log('customerExtendedData' + JSON.stringify(customerExtendedData, null, 2));
          if (accountItem?.productId == signUpProduct.id) {
            localCustomerDetails = {
              ...localCustomerDetails,
              firstName: extendedDataJson?.firstName,
              email: extendedDataJson?.emailAddress,
              lastName: extendedDataJson?.lastName
            };

            //get the plan ID from extendedData activationProductId field
            const planId = extendedDataJson?.activationProductId;
            const plan = plans.find(p => p.id == planId);
            setUserPlan(plan);
            setPlanPrice(plan.price);
          } else if (accountItem?.productId == simDispatchProduct.id) {
            setSimType("physical");
            localCustomerDetails = {
              ...localCustomerDetails,
              deliveryAddress: extendedDataJson?.deliveryAddress.replace(/[\r\n]+/g, " ")
            };
          }
          accountProductsFromConfirmedBasket.push({ productId: accountItem.productId, extendedDataJson });
        });
        setCustomerDetails(localCustomerDetails);
        //although we don't add the plan to service items any more, we still need this to handle the discount
        const serviceItems = existingConfirmedBasket?.serviceItems.map((serviceItem) => {
          const extendedData = serviceItem.extendedData ? JSON.parse(serviceItem.extendedData) : undefined;
          const prmCode = extendedData?.promoCode;
          if (prmCode) {
            setForm({ ...form, discountCode: prmCode });
            const cachedDiscount = localStorage.getItem("appliedDiscount");
            if (cachedDiscount) {
              setAppliedDiscount(JSON.parse(cachedDiscount));
              setDiscountValidated(true);
            }
          }
          serviceProductsFromConfirmedBasket.push({ productId: serviceItem.productId, extendedData });
          //The below no longer works as we don't put the plan into the basket any more, we need to pick this up from the account item extended data now
          // const idx = plans.findIndex((p) => p.id == serviceItem.productId);
          // if (idx >= 0) {
          //   setUserPlan(plans[idx]);
          // }
        });
        console.log('userPlan ' + JSON.stringify(userPlan, null, 2));
        console.log('accountProductsFromConfirmedBasket ' + JSON.stringify(accountProductsFromConfirmedBasket, null, 2));
        console.log('serviceProductsFromConfirmedBasket ' + JSON.stringify(serviceProductsFromConfirmedBasket, null, 2));
        createNewBasketFromProducts(accountProductsFromConfirmedBasket, serviceProductsFromConfirmedBasket);
      } catch (error) {
        console.log("error processing existing confirmed basket", error);
        return;
      }
    }

    if (customerDetails !== undefined) {
      setForm((f) => ({
        ...f,
        email: customerDetails?.email || userEmail || "",
        deliveryAddress: customerDetails?.deliveryAddress || "",
        firstName: customerDetails?.firstName || "",
        lastName: customerDetails?.lastName || ""
      }));
    }
  }, [userEmail, existingConfirmedBasket, signUpProduct, simDispatchProduct, plans, customerDetails]);

  useEffect(() => {
    if (promoCode) {
      applyOrRemoveDiscount();
    }
  }, []);

  const [showTooltip, setShowTooltip] = useState(false);
  const [eSimCompatible, setESimCompatible] = useState(false);

  const isMobile = window.innerWidth <= 768;

  const isRequiredInfoProvided =
    ((simType == "esim" && eSimCompatible) || (simType == "physical" && form.deliveryAddress != "")) &&
    (form.firstName != "" && form.lastName != "");

  async function handleNext() {
    setSubmitClicked(true);
    await confirmBasketAndProceed();
  }

  function handleGoBack() {
    removeDiscountsFromBasket();
    setCustomerDetails(form);
    clearConfirmedBasket();
    console.log('basket when leaving customer details is: ' + JSON.stringify(basket, null, 2));
    prev();
  }

  return (
    <>
      <HorizontalTimeline
        customerSelections={customerSelections}
        thisStep={CustomerDetails.name} // Use the component name for the step
      />
      <div className="flex-play">
        <div className="small-card">
          <div className="step-header">
            <h2>{content.customerDetails.h2}</h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", padding: 12 }}>
            <div class="flex-names">
              <input
                type="text"
                placeholder={content.customerDetails.firstNamePlaceholder}
                value={form.firstName}
                style={{ flex: 1 }}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              />
              <input class="last-name"
                type="text"
                placeholder={content.customerDetails.lastNamePlaceholder}
                value={form.lastName}
                style={{ flex: 2 }}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
              />
            </div>
            <div class="flex-row"
              style={{ alignItems: "center" }}
            >
              <input
                type="text"
                placeholder={content.customerDetails.discountCodePlaceholder}
                value={form.discountCode || ""}
                onChange={(e) => setForm({ ...form, discountCode: e.target.value })}
                style={{ flex: 2, maxWidth: "240px" }}
                disabled={discountValidated}
              />
              <button
                onClick={() => applyOrRemoveDiscount()}
                className="button button-primary"
                disabled={discountProcessing || form.discountCode === null || form.discountCode?.trim() === ""}
                style={{ flex: 0.5, height: "48px", maxWidth: "80px" }}
              >
                {discountValidated ? "Remove" : "Apply"}
              </button>
            </div>
            {(discountValidated && appliedDiscount) &&
              <p style={{ marginTop: 12, fontSize: "14px" }}>{content.customerDetails.discountCodeAppliedMessage(appliedDiscount.name)}</p>
            }
            {discountError &&
              <p style={{ marginTop: 12, fontSize: "14px", color: "red" }}>{discountError}</p>
            }
            <h3>
              {content.customerDetails.sim.h3}{" "}
              <Tooltip
                enterTouchDelay="50"
                title={content.customerDetails.sim.popUpMessage}
                arrow>
                <img src="/assets/tooltip_white_16.png" />
              </Tooltip>
            </h3>
            {/* Rounded SIM Type Toggle Switch */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                margin: "16px 0",
                fontSize: "14px",
                marginTop: "7px",
                marginBottom: "7px",
              }}
            >
              <span style={{ marginRight: 8 }}>{content.customerDetails.sim.pSIM}</span>
              <label
                style={{
                  position: "relative",
                  display: "inline-block",
                  width: "50px",
                  height: "24px",
                }}
              >
                <input
                  type="checkbox"
                  checked={simType === "esim"}
                  onChange={() =>
                    setSimType(simType === "physical" ? "esim" : "physical")
                  }
                  style={{ opacity: 0, width: 0, height: 0 }}
                  onFocus={() => {
                    simTypeSelector.current.style.border = "1px solid #fff";
                  }}
                  onBlur={() => {
                    simTypeSelector.current.style.border = "none";
                  }}
                />
                <span
                  ref={simTypeSelector}
                  style={{
                    position: "absolute",
                    cursor: "pointer",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: simType === "esim" ? "#FF6600" : "#444",
                    borderRadius: "34px",
                    transition: ".4s",
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    height: "18px",
                    width: "18px",
                    left: simType === "esim" ? "26px" : "4px",
                    bottom: "3px",
                    background: "#FFFFFF",
                    borderRadius: "50%",
                    transition: ".4s",
                  }}
                />
              </label>
              <span style={{ marginLeft: 8 }}>{content.customerDetails.sim.eSIM}</span>
            </div>
            {simType == "esim" && (
              <>
                <div>
                  <input
                    type="checkbox"
                    title="eSimCompatible"
                    onChange={(e) => setESimCompatible(e.target.checked)}
                  ></input>
                  <label style={{ fontSize: "12px" }}>
                    {content.customerDetails.sim.verificationMessage}
                  </label>
                </div>
              </>
            )}
            {simType == "physical" && (
              <>
                {/* <AddressSearchBar 
                  onChange={(e) => setForm({ ...form, deliveryAddress: e.target.value })}
                  onPlaceLoaded={(response) => {
                  setForm({ ...form, deliveryAddress: response.formatted_address })
                  }} /> */}
                <PostGridAddressAutocomplete
                  apiKey={config.postgridApiKey} // use live key in prod
                  mode="intl"            // or "standard" for US/CA
                  onSelect={(full) => {
                    // console.log("Completed address:", full);
                    console.log("Formatted address:", full.formattedAddress);
                    setForm({ ...form, deliveryAddress: full.formattedAddress });
                    // Map to your form:
                    // setValue("line1", full.address?.line1 ?? full.line1)
                    // setValue("city",  full.address?.city  ?? full.city)
                    // setValue("prov",  full.address?.prov  ?? full.state)
                    // setValue("pc",    full.address?.pc    ?? full.postal_code)
                    // setValue("country", full.address?.country ?? full.country)
                  }}
                  initialValue={customerDetails?.deliveryAddress}
                />
              </>
            )}
          </div>
          <div className="card-footer">
            <button onClick={handleGoBack} className="button button-secondary">
              {content.common.back}
            </button>
            <button
              onClick={handleNext}
              className="button button-primary"
              disabled={!isRequiredInfoProvided || submitClicked}
              style={{ position: "relative" }}
            >
              <span style={{ opacity: submitClicked ? 0 : 1 }}>
                {content.common.next}
              </span>
              {submitClicked && (
                <div style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ transform: "scale(0.4)" }}>
                    <FadeLoader color="#ffffff" />
                  </div>
                </div>
              )}
            </button>
          </div>
        </div>
        <>
          {basketLoading && !basket &&
            <FadeLoader
              color="#f36d13"
              size={200}
              loading={basketLoading}
              cssOverride={{
                justifySelf: "center",
                display: "block",
                alignSelf: "center",
                left: "20%"
              }}
            />
          }

          {(basket && userPlan) &&
            <BasketSnippet
              customerSelections={customerSelections}
              planPrice={planPrice}
              userPlan={userPlan}
              basket={basket}
              simType={simType}
              discount={appliedDiscount}
            />
          }
        </>
      </div>
    </>
  );
}
